import { db } from '../firebase';
import {
  collection,
  query,
  onSnapshot,
  where,
  updateDoc,
  doc,
  Timestamp,
  getDoc,
  getDocs,
  QueryConstraint,
  DocumentData,
  deleteDoc,
} from 'firebase/firestore';
import { UserRole } from '../types/auth.types';

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  userType: UserRole;
  status: 'active' | 'suspended' | 'inactive';
  registrationDate: Date;
  lastLogin?: Date;
  school?: string;
  grade?: number;
  phone?: string;
}

// Helper function to convert Firestore data to User type
const convertToUser = (doc: DocumentData): User => {
  const data = doc.data();
  return {
    id: doc.id,
    email: data.email,
    firstName: data.firstName,
    lastName: data.lastName,
    userType: data.userType,
    status: data.status || 'inactive',
    registrationDate: data.createdAt?.toDate() || new Date(),
    lastLogin: data.lastLogin?.toDate(),
    school: data.school,
    grade: data.grade,
  };
};

export const usersService = {
  subscribeToUsersInRegion(
    regionID: string,
    callback: (users: User[]) => void
  ) {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('regionId', '==', regionID));
    return onSnapshot(q, (snapshot) => {
      const users = snapshot.docs.map(convertToUser);
      callback(users);
    });
  },

  subscribeToUsers(callback: (users: User[]) => void) {
    const usersRef = collection(db, 'users');
    return onSnapshot(usersRef, (snapshot) => {
      const users = snapshot.docs.map(convertToUser);
      callback(users);
    });
  },

  async getUsersByRole(role: UserRole): Promise<User[]> {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('userType', '==', role));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(convertToUser);
  },

  async getUsersByRoleAndStatus(
    role: UserRole,
    status: 'active' | 'suspended' | 'inactive'
  ): Promise<User[]> {
    const usersRef = collection(db, 'users');
    const constraints: QueryConstraint[] = [
      where('userType', '==', role),
      where('status', '==', status),
    ];
    const q = query(usersRef, ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map(convertToUser);
  },

  async updateUserStatus(
    userId: string,
    status: 'active' | 'suspended' | 'inactive'
  ) {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      status,
      updatedAt: Timestamp.now(),
    });
  },

  async updateUserProfile(userId: string, updates: Partial<User>) {
    const userRef = doc(db, 'users', userId);
    const updateData = { ...updates };
    delete updateData.id; // Remove id from updates

    await updateDoc(userRef, {
      ...updateData,
      updatedAt: Timestamp.now(),
    });
  },

  async searchUsers(searchTerm: string): Promise<User[]> {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    const searchTermLower = searchTerm.toLowerCase();

    const users = snapshot.docs.map(convertToUser);

    return users.filter((user) => {
      const firstName = user.firstName?.toLowerCase() || '';
      const lastName = user.lastName?.toLowerCase() || '';
      const email = user.email.toLowerCase();

      return (
        firstName.includes(searchTermLower) ||
        lastName.includes(searchTermLower) ||
        email.includes(searchTermLower)
      );
    });
  },

  async getActiveUsersByRole(role: UserRole): Promise<User[]> {
    return this.getUsersByRoleAndStatus(role, 'active');
  },

  async changeUserRole(userId: string, newRole: any) {
    try {
      // Reference the specific document using its ID
      const userDocRef = doc(db, 'users', userId);

      //Update the userType field
      await updateDoc(userDocRef, {
        userType: newRole,
      });

      console.log('User role updated successfully');
    } catch (error) {
      console.error('Error updating user role:', error);
    }
  },
  async getUserById(uid: string): Promise<User> {
    const userDocRef = doc(db, 'users', uid);
    const userDocSnap = await getDoc(userDocRef);
    if (userDocSnap.exists()) {
      return convertToUser(userDocSnap);
    } else {
      throw new Error('User not found');
    }
  },

  //delete user document
  async deleteUserAccount(userId: string) {
    try {
      //delete the teacher's document
      await deleteDoc(doc(db, 'users', userId));
    } catch (error) {
      console.error('Error deleting user document:', error);
      throw error;
    }
  },

  async getUserAccount(userId: string) {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        return { id: userSnap.id, ...userSnap.data() };
      } else {
        console.log('No document found');
        return null; // Indicate that the document doesn't exist
      }
    } catch (error) {
      console.error('Error getting user document:', error);
      throw error;
    }
  },

  //Function to get the region ID of a user
  async getUserRegion(userId: string) {
    try {
      //Get a reference to the user document in the 'users' collection
      const userRef = doc(db, 'users', userId);
      //Fetch the document from the reference with the same User ID
      const userSnap = await getDoc(userRef);

      //Check if the document exists
      if (userSnap.exists()) {
        //Extract the data from the document
        const userData = userSnap.data();
        console.log('User region:', userData.regionId);
        //Return the region ID from the user document
        return userData.regionId;
      } else {
        console.log('No user document found with regionID feild');
        return undefined;
      }
    } catch (error) {
      console.error('Error getting user region ID:', error);
      throw error;
    }
  },
};
